import { cn } from '@app/utils/cn';
import { useProductPriceComponents } from '@app/utils/hooks';
import { useCalculateWidthOverflowing } from '@app/utils/hooks/useCalculateWidthOverflowing';
import { isDefined } from '@app/utils/is';
import { Product } from '@typings';
import React from 'react';

type Props = {
  productInfo: Product.Standard;
  isCompact?: boolean;
  parentWidth?: number;
};

export const ProductPrice = ({ productInfo, parentWidth, isCompact = false }: Props) => {
  const { formattedPrice, hasConsistentDiscountPercent, highestDiscountPercent } = useProductPriceComponents(productInfo);

  const ref = React.useRef<HTMLDivElement>(null);
  const isDiscountOverflowing = useCalculateWidthOverflowing({ ref, widthToReact: parentWidth });

  if (!isDefined(formattedPrice)) {
    return null;
  }

  const { priceBeforeDiscount } = productInfo;

  const isDiscounted = highestDiscountPercent > 0;

  if (isDiscounted) {
    return (
      <>
        <div ref={ref} className={cn('flex items-center gap-x-2 text-left !normal-case body-3', { 'gap-x-2': isCompact })}>
          <span className="text-error-45">{formattedPrice}</span>
          <div className="flex items-center gap-1">
            {hasConsistentDiscountPercent && <span className="text-neutral-60 line-through">{priceBeforeDiscount}</span>}
            {!isDiscountOverflowing && (
              <span className="flex items-center rounded-large bg-error-96 px-1 text-xs text-error-45 heading-4">
                -{highestDiscountPercent}%
              </span>
            )}
          </div>
        </div>
      </>
    );
  }

  return <div className="text-left !normal-case body-3">{formattedPrice}</div>;
};
